if (!customElements.get('mobile-dock')) {
  customElements.define(
    'mobile-dock',
    class MobileDock extends HTMLElement {
      constructor() {
        super();
    
        if (Shopify.designMode) {
          this.init();
        }
        else {
          new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
        }
      }
    
      get section() {
        return this._section = this._section || this.closest('.mobile-dock-section');
      }
    
      init() {
        if (this.initialized) return;
        this.initialized = true;
        this.setAttribute('loaded', '');

        if (this.hasAttribute('data-always-visible')) {
          this.section.classList.add('active');
        }
        else {
          this.detectForHeader();
          this.detectForFooter();
        }

        // Publish --mobile-dock-height reliably so everything that positions
        // off the dock (WhatsApp button, sticky filter, footer padding) is
        // correct on first paint — not only after a scroll. rAF covers initial
        // layout, load covers late reflow (fonts/images), matchSmall covers the
        // breakpoint switch, and a ResizeObserver keeps it in sync if the dock's
        // own height ever changes. (Previously a single setTimeout could read a
        // 0/stale height, so dependents sat in the wrong place until a scroll.)
        const setHeight = this.setHeight.bind(this);
        requestAnimationFrame(setHeight);
        window.addEventListener('load', setHeight);
        document.addEventListener('matchSmall', setHeight);
        if ('ResizeObserver' in window) {
          this._resizeObserver = new ResizeObserver(setHeight);
          this._resizeObserver.observe(this);
        }

        this.followVisualViewport();
    
        if (Shopify.designMode) {
          this.section.addEventListener('shopify:section:select', () => {
            this.section.classList.add('shopify-active');
          });
          this.section.addEventListener('shopify:section:deselect', () => {
            this.section.classList.remove('shopify-active');
          });
        }
      }
    
      detectForHeader() {
        const header = document.querySelector('.header-section');
        if (header === null) {
          this.section.classList.add('active');
          return;
        }

        // "Always visible" OFF → smart show/hide on scroll, INDEPENDENT of the
        // header's sticky setting: show when scrolling down, hide when scrolling
        // up or near the top. (Previously this was coupled to .header-hidden;
        // now the header and dock are decoupled, so the dock owns this logic.)
        this.topThreshold = parseInt(header.getBoundingClientRect().bottom) || 0;
        this.lastScrollY = window.scrollY;
        window.addEventListener('scroll', theme.utils.throttle(this.onScrollForHeader.bind(this)), false);
      }

      onScrollForHeader() {
        const y = Math.max(0, window.scrollY);
        if (y <= this.topThreshold) {
          this.section.classList.remove('active');        // near the top → hide
        }
        else if (y > this.lastScrollY + 2) {
          this.section.classList.add('active');           // scrolling down → show
        }
        else if (y < this.lastScrollY - 2) {
          this.section.classList.remove('active');        // scrolling up → hide
        }
        this.lastScrollY = y;
      }
    
      detectForFooter() {
        const footer = document.querySelector('.footer-copyright');
        if (footer === null) return;
    
        window.addEventListener('scroll', theme.utils.throttle(this.onScrollForFooter.bind(this)), false);
      }
    
      onScrollForFooter() {
        if (!theme.config.mqlSmall) return;
        
        const scrolledTo = window.scrollY + window.innerHeight;
        const threshold = this.offsetHeight;
        const isReachBottom = document.body.scrollHeight - threshold <= scrolledTo;
        this.classList.toggle('active', isReachBottom);
      }
    
      // iOS Safari: `position: fixed; bottom` resolves against the layout
      // viewport, which can end below what is on screen (collapsing toolbar),
      // so an edge-to-edge dock floated with page content showing under it.
      // Publish the gap between the bottom of the fixed containing block (a
      // zero-width fixed probe, which also leaves out a horizontal scrollbar)
      // and the bottom of the visual viewport as --mobile-dock-offset; the
      // dock, the WhatsApp float and the sticky bars translate up by it. 0
      // whenever the two match (desktop, Android, most of the time), while
      // pinch-zoomed, and while a text field has focus (an on-screen keyboard
      // shrinks the visual viewport too — the dock stays behind it, as before).
      followVisualViewport() {
        const vv = window.visualViewport;
        if (!vv || this._vvUpdate) return;

        const probe = document.createElement('span');
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText = 'position:fixed;top:0;bottom:0;left:0;width:0;visibility:hidden;pointer-events:none';
        this.section.appendChild(probe);
        this._vvProbe = probe;

        const root = document.documentElement;
        const noKeyboard = /^(checkbox|radio|button|submit|reset|range|color|file|image)$/;
        let frame = 0;
        let last = null;

        const update = () => {
          frame = 0;
          const el = document.activeElement;
          const typing = el && (el.isContentEditable || el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' && !noKeyboard.test(el.type)));
          let gap = 0;
          if (!typing && vv.scale <= 1.01) {
            gap = probe.getBoundingClientRect().bottom - (vv.height + vv.offsetTop);
          }
          gap = Math.max(0, Math.round(gap));
          if (gap !== last) {
            last = gap;
            root.style.setProperty('--mobile-dock-offset', `${gap}px`);
          }
        };
        const schedule = () => {
          if (!frame) frame = requestAnimationFrame(update);
        };

        this._vvUpdate = schedule;
        vv.addEventListener('resize', schedule, { passive: true });
        vv.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('scroll', schedule, { passive: true });
        document.addEventListener('focusin', schedule);
        document.addEventListener('focusout', schedule);
        update();
      }

      connectedCallback() {
        if (this.initialized) this.followVisualViewport();
      }

      disconnectedCallback() {
        const schedule = this._vvUpdate;
        if (!schedule) return;
        const vv = window.visualViewport;
        vv.removeEventListener('resize', schedule);
        vv.removeEventListener('scroll', schedule);
        window.removeEventListener('scroll', schedule);
        document.removeEventListener('focusin', schedule);
        document.removeEventListener('focusout', schedule);
        this._vvProbe?.remove();
        this._vvUpdate = this._vvProbe = null;
        document.documentElement?.style.removeProperty('--mobile-dock-offset');
      }

      setHeight() {
        document.documentElement.style.setProperty('--mobile-dock-height', `${this.getBoundingClientRect().height.toFixed(1)}px`);
      }
    }, { extends: 'nav' }
  );
}
